import type { CheckoutLanguage } from '../components/checkoutLabels';

// The order confirmation's own sentences, in each language a store may be served in. A sentence that holds
// the buyer's phone or the total is kept as the words before and after it, so the value can stand out.

/** Words before and after a value set in bold. */
type Around = readonly [string, string];

/** Every sentence the confirmation prints. */
export type OrderWords = {
  whatNext: string; steps: readonly { title: string; text: string }[];
  callYou: Around; payCash: Around; paymentRecorded: Around; cashOnDelivery: string;
  noOrderTitle: string; noOrderText: string; orderNumber: string; thankYou: string;
};

/** The confirmation in English. `{number}` and `{name}` are replaced. */
const EN: OrderWords = {
  whatNext: 'What happens next',
  steps: [{ title: 'We call you', text: 'to confirm your order and your address.' }, { title: 'Your parcel ships', text: 'and is delivered to your door.' }, { title: 'You pay on delivery', text: 'in cash, once you have checked it.' }],
  callYou: ['We will call you on ', ' to confirm your delivery.'], payCash: ['Pay ', ' in cash when your parcel arrives.'], paymentRecorded: ['Your payment of ', ' is recorded.'],
  cashOnDelivery: 'Cash on delivery', noOrderTitle: 'There is no order to show here yet.', noOrderText: 'Your confirmation appears on this page right after you place an order.',
  orderNumber: 'Order {number}', thankYou: 'Thank you, {name}!'
};

/** The confirmation in French. */
const FR: OrderWords = {
  whatNext: 'Et maintenant',
  steps: [{ title: 'Nous vous appelons', text: 'pour confirmer votre commande et votre adresse.' }, { title: 'Votre colis est expédié', text: 'et livré à votre porte.' }, { title: 'Vous payez à la livraison', text: 'en espèces, après l’avoir vérifié.' }],
  callYou: ['Nous vous appellerons au ', ' pour confirmer votre livraison.'], payCash: ['Payez ', ' en espèces à la réception de votre colis.'], paymentRecorded: ['Votre paiement de ', ' est enregistré.'],
  cashOnDelivery: 'Paiement à la livraison', noOrderTitle: 'Aucune commande à afficher pour l’instant.', noOrderText: 'Votre confirmation s’affiche ici juste après votre commande.',
  orderNumber: 'Commande {number}', thankYou: 'Merci, {name} !'
};

/** The confirmation in Arabic. */
const AR: OrderWords = {
  whatNext: 'ما الذي سيحدث بعد ذلك',
  steps: [{ title: 'نتصل بك', text: 'لتأكيد طلبك وعنوانك.' }, { title: 'يُشحن طردك', text: 'ويصل إلى باب منزلك.' }, { title: 'تدفع عند الاستلام', text: 'نقدًا بعد التحقق منه.' }],
  callYou: ['سنتصل بك على ', ' لتأكيد التوصيل.'], payCash: ['ادفع ', ' نقدًا عند استلام طردك.'], paymentRecorded: ['تم تسجيل دفعتك البالغة ', '.'],
  cashOnDelivery: 'الدفع عند الاستلام', noOrderTitle: 'لا يوجد طلب لعرضه هنا بعد.', noOrderText: 'يظهر تأكيد طلبك هنا مباشرة بعد إتمامه.',
  orderNumber: 'الطلب {number}', thankYou: 'شكرًا لك، {name}!'
};

/** Every language's confirmation. */
const WORDS: Readonly<Record<CheckoutLanguage, OrderWords>> = { en: EN, fr: FR, ar: AR };

// Picks the confirmation's words for a store's language, English when it has chosen none.
export const orderWords = (language: CheckoutLanguage | null | undefined): OrderWords => WORDS[language ?? 'en'];
